import React, { useState } from "react"
import { Link } from "react-router-dom"
import Rating from "./Rating"
import { ChevronsLeft, ChevronsRight } from "lucide-react"
import { formatCurrency } from "../../util"
import "../styles/UserDetails.css"

function UserDetails({ user }) {
  const [selectedTab, setSelectedTab] = useState(1)
  const [expandedReviews, setExpandedReviews] = useState(new Set())

  if (!user) return null

  const toggleReviewExpansion = (reviewId) => {
    setExpandedReviews((prevExpanded) => {
      const newExpanded = new Set(prevExpanded)
      if (newExpanded.has(reviewId)) {
        newExpanded.delete(reviewId)
      } else {
        newExpanded.add(reviewId)
      }
      return newExpanded
    })
  }

  return (
    <div className="userDetails-container">
      <h2 className="userDetailsHeading">{user.username}'s Details</h2>
      <div className="userDetailsTabs">
        <a
          className={`userDetailsTab ${selectedTab === 1 && "tabActive"}`}
          onClick={() => setSelectedTab(1)}
        >
          Orders
        </a>
        <a
          className={`userDetailsTab ${selectedTab === 2 && "tabActive"}`}
          onClick={() => setSelectedTab(2)}
        >
          Cart
        </a>
        <a
          className={`userDetailsTab ${selectedTab === 3 && "tabActive"}`}
          onClick={() => setSelectedTab(3)}
        >
          Reviews
        </a>
      </div>
      <div className="userDetailsCard">
        <div className="userDetailsCardBody">
          {selectedTab === 1 && (
            <div className="overflow-x-auto">
              <table className="table-xs table">
                <thead>
                  <tr className="orders-table-header">
                    <th>Id</th>
                    <th>Created At</th>
                    <th>Total Items</th>
                    <th>Total Price</th>
                  </tr>
                </thead>
                <tbody>
                  {user.orders &&
                    user.orders
                      .filter((order) => {
                        return order.isCart !== true
                      })
                      .map((order) => {
                        const totalItems = order.lineItems.reduce(
                          (acc, item) => acc + item.quantity,
                          0
                        )
                        const totalPrice = order.lineItems.reduce(
                          (acc, item) =>
                            acc + item.quantity * item.product.price,
                          0
                        )
                        return (
                          <tr key={order.id} className="userLineItemDetails">
                            <td>{order.id}</td>
                            <td>
                              {new Date(order.createdAt).toLocaleString()}
                            </td>
                            <td>{totalItems}</td>
                            <td>{formatCurrency(totalPrice)}</td>
                          </tr>
                        )
                      })}
                </tbody>
              </table>
            </div>
          )}
          {selectedTab === 2 && (
            <div className="overflow-x-auto">
              <div className="flex flex-row">
                <div className="usersCartTotalItems">
                  Total Items:{" "}
                  {user.cart.cartItems.length > 0
                    ? user.cart.cartItems.length
                    : 0}
                </div>
                <div className="usersCartTotalPrice">
                  Total Price:{" "}
                  {formatCurrency(
                    user.cart.cartItems.reduce(
                      (acc, item) => acc + item.quantity * item.product.price,
                      0
                    )
                  )}
                </div>
              </div>
              <table className="cart-details-header">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Added On</th>
                    <th>Quantity</th>
                    <th>Total Price</th>
                  </tr>
                </thead>
                <tbody>
                  {user.cart.cartItems &&
                    user.cart.cartItems.map((item) => {
                      return (
                        <tr key={item.id} className="userLineItemDetails">
                          <td>
                            <Link
                              to={`/products/${item.product.id}`}
                              className="link-primary link"
                            >
                              {item.product.name}
                            </Link>
                          </td>
                          <td>{new Date(item.createdAt).toLocaleString()}</td>
                          <td>{item.quantity}</td>
                          <td>
                            {formatCurrency(item.quantity * item.product.price)}
                          </td>
                        </tr>
                      )
                    })}
                </tbody>
              </table>
            </div>
          )}
          {selectedTab === 3 && (
            <div className="overflow-x-auto">
              <table className="table-xs table">
                <thead>
                  <tr className="reviews-details-header">
                    <th>Product</th>
                    <th>Created At</th>
                    <th>Rating</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {user.reviews &&
                    user.reviews.map((review) => {
                      const isExpanded = expandedReviews.has(review.id)
                      const words = review.description.split(" ")
                      const shouldTruncate = words.length > 10
                      const displayedDescription =
                        isExpanded || !shouldTruncate
                          ? review.description
                          : words.slice(0, 10).join(" ") + "... "

                      return (
                        <tr key={review.id} className="userLineItemDetails">
                          <td>
                            <Link to={`/products/${review.product.id}`}>
                              {review.product.name}
                            </Link>
                          </td>
                          <td>
                            {new Date(review.createdAt).toLocaleDateString()}
                          </td>
                          <td>
                            <Rating rating={review.rating} />
                          </td>
                          <td>
                            <span>{displayedDescription}</span>
                            {shouldTruncate && (
                              <button
                                className="readMoreButton"
                                onClick={() => toggleReviewExpansion(review.id)}
                              >
                                {isExpanded ? (
                                  <>
                                    <ChevronsLeft size={20} />
                                    <span>Read Less</span>
                                  </>
                                ) : (
                                  <>
                                    <span>Read More</span>
                                    <ChevronsRight size={20} />
                                  </>
                                )}
                              </button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default UserDetails
